<template>
    <div class="action-error-content">
        <p class="action-error-message">{{ resolvedMessage }}</p>

        <div v-if="resolvedErrorCode" class="action-error-code">
            <span class="action-error-code-label">{{ $t("Error code") }}</span>
            <code class="action-error-code-value">{{ resolvedErrorCode }}</code>
        </div>

        <CodeBlock
            v-if="hasErrorDetails"
            :code="formatErrorDetails(resolvedErrorDetails)"
            :language="errorDetailsLanguage"
            :name="errorDetailsName"
        ></CodeBlock>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { CodeBlockLanguage } from "@/components/utils/CodeBlock.vue";
import CodeBlock from "@/components/utils/CodeBlock.vue";

export default defineComponent({
    components: {
        CodeBlock,
    },
    name: "ActionErrorModalContent",
    emits: ["update:display"],
    props: {
        display: Boolean,
        sharedData: {
            type: Object,
            default: () => ({}),
        },
        message: String,
        errorCode: String,
        errorMessage: {
            type: [Object, Array, String, Number, Boolean] as PropType<unknown>,
            default: null,
        },
    },
    computed: {
        resolvedErrorCode: function (): string {
            return String(this.sharedData.actionErrorCode || this.errorCode || "");
        },

        resolvedErrorDetails: function (): unknown {
            return this.sharedData.actionErrorMessage ?? this.errorMessage;
        },

        resolvedMessage: function (): string {
            if (this.message) {
                return this.message;
            }

            if (this.resolvedErrorCode) {
                return this.$t("Could not complete the action due to an error in the remote participant");
            }

            return this.$t("Could not complete the action due to an error");
        },

        hasErrorDetails: function (): boolean {
            return this.resolvedErrorDetails !== undefined && this.resolvedErrorDetails !== null && this.resolvedErrorDetails !== "";
        },

        errorDetailsLanguage: function (): CodeBlockLanguage {
            return typeof this.resolvedErrorDetails === "string" ? "raw" : "json";
        },

        errorDetailsName: function (): string {
            return typeof this.resolvedErrorDetails === "string" ? this.$t("Error details") : this.$t("Remote error message");
        },
    },
    methods: {
        formatErrorDetails: function (msg: unknown): string {
            if (typeof msg === "string") {
                return msg;
            }

            try {
                return JSON.stringify(msg, null, 4);
            } catch {
                return String(msg);
            }
        },
    },
    mounted: function () {
        if (this.display) {
            this.$autoFocus();
        }
    },
    watch: {
        display: function () {
            if (this.display) {
                this.$autoFocus();
            }
        },
    },
});
</script>

<style scoped>
.action-error-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-3, 0.75rem);
    min-width: 0;
}

.action-error-message {
    margin: 0;
    color: var(--color-theme-neutral-600, #747daf);
    font-size: var(--font-size-action-modal, 14px);
    line-height: 1.5;
    white-space: pre-line;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.action-error-code {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-2, 0.5rem);
}

.action-error-code-label {
    font-size: var(--font-size-action-modal, 14px);
    font-weight: 600;
}

.action-error-code-value {
    font-family: var(--font-family-mono, monospace);
    font-size: 13px;
    padding: 2px 8px;
    border-radius: var(--rounded-md, 0.375rem);
    border: var(--border-width-base, 1px) solid var(--color-border-danger, #fda5a5);
    background: var(--color-background-danger-subtlest, #fef2f2);
    color: var(--color-text-danger, #c10007);
    overflow-wrap: anywhere;
    word-break: break-word;
}
</style>
